import { Canvas, FabricImage } from 'fabric';
import {
  createChromaKeyInstance,
  destroyChromaKeyInstance,
  drawChromaKey,
  type ChromaKeyInstance
} from '../../shaders/utils/chroma-key-instance';
import { applyModelToFabric } from '../../model/converters/model-to-fabric';
import { readCanonicalTransforms, writeTransform } from '../../model/converters/read-canonical-transforms';
import { getObjectsByLayerId } from '../../model/layer-id';
import type { CanonicalTransforms, FrameState } from '../../model/resolve-frame';
import type { PieceModel } from '../../model/types';

const SEEK_TIMEOUT_MS = 5000;
const SEEK_EPSILON_S = 0.001;

export type OffscreenRenderer = {
  /** Frame source for the encoder. */
  element: HTMLCanvasElement;
  canonical: CanonicalTransforms;
  drawFrame: (frame: FrameState) => Promise<void>;
  dispose: () => void;
};

const seekVideo = (video: HTMLVideoElement, timeS: number): Promise<void> => {
  const limit = Math.max(0, (video.duration || timeS) - SEEK_EPSILON_S);
  const target = Math.min(timeS, limit);
  if (Math.abs(video.currentTime - target) < SEEK_EPSILON_S) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const onSeeked = () => {
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(() => {
      video.removeEventListener('seeked', onSeeked);
      reject(new Error(`Video seek to ${target}s timed out`));
    }, SEEK_TIMEOUT_MS);

    video.addEventListener('seeked', onSeeked, { once: true });
    video.currentTime = target;
  });
};

/**
 * A drawing surface the export owns, separate from the one the user edits. Mounts
 * the model once, then draws any frame on demand once the videos land on it.
 */
export const createOffscreenRenderer = async (model: PieceModel): Promise<OffscreenRenderer> => {
  const element = document.createElement('canvas');
  element.width = model.width;
  element.height = model.height;

  // Fabric keeps this element as its lower canvas, so it doubles as the frame source.
  const canvas = new Canvas(element, {
    width: model.width,
    height: model.height,
    renderOnAddRemove: false,
    selection: false,
    enableRetinaScaling: false
  });

  // Browsers throttle decoders for detached video elements, which would slow every seek.
  const mediaContainer = document.createElement('div');
  mediaContainer.setAttribute('aria-hidden', 'true');
  mediaContainer.style.cssText = 'position:fixed;left:-99999px;top:0;width:0;height:0;overflow:hidden';
  document.body.appendChild(mediaContainer);

  const videos = new Map<string, HTMLVideoElement>();
  const pending: Promise<unknown>[] = [];

  applyModelToFabric(
    canvas,
    model,
    (id, video) => {
      video.muted = true;
      mediaContainer.appendChild(video);
      videos.set(id, video);
    },
    promise => {
      pending.push(promise);
      return promise;
    }
  );

  // Media lands asynchronously and queues more work as it does.
  while (pending.length > 0) {
    await Promise.all(pending.splice(0));
  }

  // The object set is fixed once mounted, so index it once instead of per frame.
  const objects = getObjectsByLayerId(canvas);

  // Chroma-keyed videos draw through a shader onto their own canvas, which Fabric
  // takes as the image source. Here it runs once per printed frame, not on a loop.
  const chromaKeys: ChromaKeyInstance[] = [];
  for (const id of model.layerOrder) {
    const layer = model.layers[id];
    if (layer?.kind !== 'video' || !layer.content.chromaKey) continue;

    const video = videos.get(`video_${id}`);
    const obj = objects.get(id);
    if (!video || !obj) continue;

    const instance = createChromaKeyInstance(video);
    if (!instance) continue;

    instance.params = layer.content.chromaKey;
    (obj as FabricImage).setElement(instance.canvas);
    chromaKeys.push(instance);
  }

  canvas.renderAll();

  const drawFrame = async (frame: FrameState): Promise<void> => {
    const seeks: Promise<void>[] = [];

    for (const layer of frame.layers) {
      const obj = objects.get(layer.id);
      if (!obj) continue;

      obj.visible = layer.visible;
      writeTransform(obj, layer.transform);

      if (layer.mediaTime !== undefined) {
        const video = videos.get(`video_${layer.id}`);
        if (video) seeks.push(seekVideo(video, layer.mediaTime));
      }
    }

    await Promise.all(seeks);
    chromaKeys.forEach(drawChromaKey);
    canvas.renderAll();
  };

  const dispose = (): void => {
    chromaKeys.forEach(destroyChromaKeyInstance);
    videos.forEach(video => {
      video.pause();
      video.removeAttribute('src');
      video.load();
    });
    videos.clear();
    mediaContainer.remove();
    void canvas.dispose();
  };

  return { element, canonical: readCanonicalTransforms(canvas), drawFrame, dispose };
};
